import React, { useState } from 'react';
import { LayoutGrid, Plus, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import { useCreateColumnMutation } from '../../api/taskColumnsApi';
import { apiErrorMessage } from '../../utils/apiError';

// The board genuinely starts empty, so the first thing a clinic sees has to be a working board
// rather than a blank page and a builder to learn. These are the columns the clinic described.
const STARTERS = [
  {
    name: 'Idle 3 days',
    ruleKind: 'PatientIdleSinceVisit',
    parameters: { op: 'Eq', daysAgo: '3', requireNothingBooked: 'true' },
    blurb: 'Last appointment 3 days ago, nothing booked since',
  },
  {
    name: 'Callbacks due',
    ruleKind: 'LeadFollowUpDue',
    parameters: { includeOverdue: 'false' },
    blurb: 'Leads whose promised callback lands on the board date',
  },
  {
    name: 'Finished their course',
    ruleKind: 'PatientCourseCompleted',
    parameters: { op: 'Eq', daysAgo: '7' },
    blurb: 'Course completed a week ago — the renewal conversation',
  },
  {
    name: 'In today, owes money',
    ruleKind: 'PatientAppointmentWithDues',
    parameters: { minDue: '1' },
    blurb: 'Visiting on the board date with an outstanding balance',
  },
];

export default function BoardEmptyState({ onAddColumn }) {
  const [createColumn] = useCreateColumnMutation();
  const [busy, setBusy] = useState(false);

  const addStarters = async () => {
    setBusy(true);
    try {
      for (const s of STARTERS) {
        // Sequential on purpose: Position is assigned from the current max, so parallel creates
        // would race for the same slot.
        // eslint-disable-next-line no-await-in-loop
        await createColumn({ name: s.name, ruleKind: s.ruleKind, parameters: s.parameters }).unwrap();
      }
      toast.success('Board set up — edit or delete any column from its menu.');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="empty-state" style={{ padding: '32px 20px' }}>
          <LayoutGrid className="es-icon" size={44} />
          <h3>Your board is empty</h3>
          <p style={{ maxWidth: 560 }}>
            Each column is a question about who needs contacting — "patients who finished their course
            a week ago", "leads due a callback today". Set the board date once and every column
            answers for that day.
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 6 }}>
            <button className="btn btn-primary" onClick={addStarters} disabled={busy}>
              <Sparkles size={15} /> {busy ? 'Setting up…' : 'Start with 4 common columns'}
            </button>
            <button className="btn btn-outline" onClick={onAddColumn}>
              <Plus size={15} /> Build my own
            </button>
          </div>

          <ul className="starter-list">
            {STARTERS.map((s) => (
              <li key={s.name}><strong>{s.name}</strong> — {s.blurb}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
